import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { GENERIC_PACK, type StatePack } from '@/states/types'

const StateContext = createContext<StatePack>(GENERIC_PACK)

export function StateProvider({
  pack,
  children,
}: {
  pack: StatePack
  children: ReactNode
}) {
  return <StateContext.Provider value={pack}>{children}</StateContext.Provider>
}

export function useStatePack(): StatePack {
  return useContext(StateContext)
}

/**
 * Hook for slide components to look up state-specific overrides for their
 * interactive examples. Returns `null` when there's no override for this slide.
 *
 * Usage in a slide component:
 *   const override = useStateExample<{ bankName: string }>('pv-call-bank')
 *   const bankName = override?.bankName ?? 'your bank'
 */
export function useStateExample<T = unknown>(slideId: string): T | null {
  const pack = useStatePack()
  return useMemo(() => {
    const overrides = pack.exampleOverrides
    if (!overrides) return null
    return (overrides[slideId] as T | undefined) ?? null
  }, [pack, slideId])
}
